import { endOfDay, format, parseISO, startOfDay } from 'date-fns';
import datepicker from 'js-datepicker';
import { forwardRef, useEffect, useRef } from 'react';

import { Calendar } from '../../icons';
import { Input } from '../Input';

import 'js-datepicker/dist/datepicker.min.css';
import './override.css';

import type { TrackingProps } from '../types';
import type { IsoDate } from '@insify/global-date';
import type { SetRequired } from 'type-fest';

export type DatepickerProps = SetRequired<
  Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onSelect' | 'children'>,
  'name'
> &
  Partial<TrackingProps> & {
    locale: 'nl' | 'de' | 'fr';
    error?: string;
    label?: string;
    onSelect?: (date: IsoDate) => void;
    minDate?: Date;
    maxDate?: Date;
    value?: string;
    alwaysShow?: boolean;
  };

const regionalSettings = {
  nl: {
    customDays: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
    customMonths: [
      'januari',
      'februari',
      'maart',
      'april',
      'mei',
      'juni',
      'juli',
      'augustus',
      'september',
      'oktober',
      'november',
      'december',
    ],
  },
  de: {
    customDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    customMonths: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  },
  fr: {
    customDays: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'],
    customMonths: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
  },
};

export const Datepicker = forwardRef<HTMLInputElement, DatepickerProps>(
  ({ value, onSelect, minDate, maxDate, alwaysShow, ...props }, forwardedRef) => {
    const ref = useRef<HTMLInputElement | null>(null);
    const pickerRef = useRef<ReturnType<typeof datepicker>>(null);
    const valueRef = useRef(value);

    const isValid = (date: Date | undefined) => {
      return (
        (minDate === undefined || date === undefined || date >= startOfDay(minDate)) &&
        (maxDate === undefined || date === undefined || date <= endOfDay(maxDate))
      );
    };

    useEffect(() => {
      const selectedDate = value ? parseISO(value) : undefined;
      const picker = datepicker(ref.current, {
        onSelect: (_: unknown, date: Date) => {
          const isoDateString = format(date, 'yyyy-MM-dd') as IsoDate;
          valueRef.current = isoDateString;
          onSelect && onSelect(isoDateString);
        },
        startDay: 1,
        alwaysShow,
        dateSelected: selectedDate && isValid(selectedDate) ? selectedDate : undefined,
        disableYearOverlay: true,
        minDate: minDate ? startOfDay(minDate) : undefined,
        maxDate: maxDate ? endOfDay(maxDate) : undefined,
        formatter: (input: HTMLInputElement, date: Date) => {
          input.value = Intl.DateTimeFormat(props.locale, { dateStyle: 'short' }).format(date);
        },
        ...regionalSettings[props.locale],
      });
      pickerRef.current = picker;

      return () => {
        picker.remove();
        pickerRef.current = null;
      };
    }, []);

    useEffect(() => {
      if (pickerRef.current) {
        if (valueRef.current && isValid(parseISO(valueRef.current))) {
          pickerRef.current.setDate(parseISO(valueRef.current));
        } else {
          pickerRef.current.setDate(undefined);
          valueRef.current = undefined;
        }
        if (minDate) {
          pickerRef.current.setMin(startOfDay(minDate));
        }
        if (maxDate) {
          pickerRef.current.setMax(endOfDay(maxDate));
        }
      }
    }, [minDate && format(minDate, 'yyyy-MM-dd'), maxDate && format(maxDate, 'yyyy-MM-dd')]);

    return (
      <Input
        readOnly
        after={<Calendar />}
        ref={(el) => {
          ref.current = el;
          if (typeof forwardedRef === 'function') {
            forwardedRef(el);
          } else if (forwardedRef) {
            forwardedRef.current = el;
          }
        }}
        {...props}
      />
    );
  },
);
